import { useTranslation } from "react-i18next";
import { ViewAction } from "@/components/table/actions";

import { useState } from "react";
import { format as formatDate } from "date-fns";
import { Table } from "@/components/table";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ChevronLeft, UserIcon } from "lucide-react";
import type { TFunction } from "i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, CardHeader } from "@/components/ui/card";
import { startCase } from "lodash";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import FilterButton from "@/components/table/FilterButton";
import FiltersModal from "@/components/FiltersModal";
import { usePerformanceCycleParams } from "./performance.params";
import type { ITeamMember } from "@/types/(company)/performance.interface";
import { useGetPerformanceCyclesByMemberIdQuery } from "@/services/(company)/api.performance";
import { ENUM_PERFORMANCE_CYCLE_STATUS } from "@/utils/enums/performance.enum";
import { PATHS } from "@/routes/paths";
import PerformanceCycleFilter from "./performance-cycle-filter";

const buildColumns = (translate: TFunction) => [
  { key: "cycleName", label: translate("company:performance.col_cycle_name") },
  { key: "cycleType", label: translate("company:performance.col_cycle_type") },
  { key: "startDate", label: translate("company:performance.col_start_date") },
  { key: "endDate", label: translate("company:performance.col_end_date") },
  { key: "status", label: translate("company:performance.col_status") },
  {
    key: "actions",
    label: translate("company:performance.col_actions"),
    className: "w-1",
  },
];

export const PerformanceCyclesList = () => {
  const { t: translate } = useTranslation(["company", "common"]);
  const columns = buildColumns(translate);
  const { id, memberId } = useParams<{ id: string; memberId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const member = location.state?.member as ITeamMember | undefined;

  // Filters and page live in the URL — see useTableParams.
  const t = usePerformanceCycleParams();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const { data, isFetching } = useGetPerformanceCyclesByMemberIdQuery(
    {
      memberId: memberId!,
      limit: 10,
      page: t.page,
      ...t.apiParams,
    },
    { skip: !memberId },
  );

  const rows =
    data?.data?.map((cycle) => ({
      id: cycle.id,
      cycleName: cycle.configuration?.name ?? "-",
      cycleType: startCase(cycle.cycleType) ?? "-",
      startDate: cycle.startDate
        ? formatDate(cycle.startDate, "dd MMMM, yyyy")
        : "-",
      endDate: cycle.endDate ? formatDate(cycle.endDate, "dd MMMM, yyyy") : "-",
      status: <StatusPill status={cycle.status} />,
      actions: (
        <div className="flex gap-2 items-center justify-end">
          <ViewAction
            onClick={() =>
              navigate(
                PATHS.COMPANY.COMPANY_PERFORMANCE_PATHS.PERFORMANCE_EVALUATORS(
                  id!,
                  cycle.id,
                ),
              )
            }
            tooltip={translate("company:performance.view_evaluators")}
          />
        </div>
      ),
    })) ?? [];

  return (
    <section className="flex flex-col gap-4 bg-white p-4 rounded-sm">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="h-9 w-9 flex items-center justify-center rounded-xs border border-border shrink-0"
        >
          <ChevronLeft size={18} className="text-primary rtl:rotate-180" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          {translate("company:performance.list_of_performance_cycles")}
        </h1>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3 bg-info-muted p-4">
          <div className="flex items-center gap-3">
            <Avatar className="shrink-0">
              <AvatarImage src={member?.user.profileImage || undefined} />
              <AvatarFallback className="bg-border">
                <UserIcon size={12} className="text-gray-500" />
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-medium text-gray-800">
                {member?.user.fullName ?? "-"}
              </p>
              <p className="text-xs text-gray-500">
                {translate("company:performance.role_label", {
                  role: member?.role?.roleName ?? "-",
                })}
              </p>
            </div>
          </div>

          <FilterButton
            filtersCount={t.activeCount}
            onClick={() => setIsFilterModalOpen(true)}
            onReset={t.reset}
            className="bg-white/80"
          />
        </CardHeader>

        <Table
          columns={columns}
          data={rows}
          pagination={{
            totalPages: data?.meta?.totalPages ?? 0,
            currentPage: t.page,
            onPageChange: t.setPage,
          }}
          loading={isFetching}
          onRowClick={(row) =>
            navigate(
              PATHS.COMPANY.COMPANY_PERFORMANCE_PATHS.PERFORMANCE_EVALUATORS(
                id!,
                row.id,
              ),
            )
          }
        />
      </Card>

      <FiltersModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        initialFilters={t.filters}
        defaultFilters={t.defaults}
        onApply={(newFilters) => {
          t.applyFilters(newFilters);
          setIsFilterModalOpen(false);
        }}
        renderFilters={({ filters, onChange }) => (
          <PerformanceCycleFilter filters={filters} onChange={onChange} />
        )}
      />
    </section>
  );
};

// ─── Status Pill ─────────────────────────────────────────────────────────────

const StatusPill = ({ status }: { status: ENUM_PERFORMANCE_CYCLE_STATUS }) => {
  const statusPills: Record<ENUM_PERFORMANCE_CYCLE_STATUS, string> = {
    [ENUM_PERFORMANCE_CYCLE_STATUS.COMPLETED]:
      "bg-green-100 text-green-700 border border-green-300",
    [ENUM_PERFORMANCE_CYCLE_STATUS.IN_PROGRESS]:
      "bg-info-muted text-info border border-blue-300",
    [ENUM_PERFORMANCE_CYCLE_STATUS.PENDING]:
      "bg-orange-50 text-orange-500 border border-orange-300",
  };

  const className =
    statusPills[status] ?? "bg-gray-100 text-gray-600 border border-border";

  return (
    <Badge className={`font-medium rounded-sm ${className}`}>
      <CheckCircle2 className="size-3.5 me-1" />
      {startCase(status)}
    </Badge>
  );
};
